import {
  DESTINATION_QUEUE_ERROR,
  DESTINATION_QUEUE_MAIN,
} from '@1po/1po-bff-fe-spec/generated/common/WebsocketConstants';
import { Client, Frame } from '@stomp/stompjs';
import { AnyAction, Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from 'app/AppStore';
import { getToken } from 'domains/auth/Auth.util';
import { shouldRestoreDelegationSession } from 'domains/delegation/DelegationSession.restore';
import { getActiveDelegationSessionRequest, setLoading } from 'domains/delegation/DelegationSession.store';
import { getWebLanguage } from 'domains/user';
import {
  getSelectedProfile,
  getUserProfileReconnectSaga,
  getUserProfileRequestSaga,
  reloadSubscriptionBeUserSaga,
  setTokenExpired,
} from 'domains/user/User.store';
import { checkAddSetIsExpired } from 'domains/webSockets/utils/utils';
import {
  setConnected,
  setErrorNoConnectionToBESide,
  setWsConnectInitialized,
  setWsUserSessionInitialized,
} from 'domains/webSockets/WebSocket.store';
import feVersion from 'VERSION.json';
import { onMessageReceived } from './messageHandlers';

export const beforeConnect = async (
  stompClient: Client,
  dispatch: Dispatch,
  getState: () => RootState,
  setConnectedFunc: (b: boolean) => AnyAction,
): Promise<void> => {
  console.log('STOMP - before connect');
  const token = await getToken();
  const profile = getSelectedProfile(getState());
  const webLanguage = getWebLanguage(getState());
  const tabVisible = document.visibilityState === 'visible';
  if (!token || !profile || !tabVisible) {
    await stompClient.deactivate();
    // No auth token — user is logged out. Do not attempt reconnect.
    if (!token) {
      return;
    }
    // Profile is still loading or tab is hidden — retry after a short delay.
    setTimeout(() => {
      stompClient.activate();
    }, 500);
    return;
  }
  stompClient.configure({
    connectHeaders: {
      'trace-id': uuidv4(),
      'x-auth-token': token,
      'selected-profile': profile.id,
      'app-version': feVersion.version,
      'web-language': webLanguage.toString(),
    },
  });

  const isExpired = await checkAddSetIsExpired(dispatch);
  if (isExpired) {
    dispatch(setConnectedFunc(false));
    await stompClient.deactivate();
  }
};

export const onConnect = (stompClient: Client, dispatch: Dispatch, getState: () => RootState, frame: Frame): void => {
  console.log('STOMP - connection success', frame);
  const activeDelegationSession = getState().delegationSession?.activeSession?.data;
  const isReconnect = getState().websockets.wsHasEverConnected;

  dispatch(setConnected(true));
  if (isReconnect) {
    dispatch(getUserProfileReconnectSaga());
  } else {
    dispatch(getUserProfileRequestSaga());
  }
  dispatch(reloadSubscriptionBeUserSaga());
  if (shouldRestoreDelegationSession(activeDelegationSession)) {
    dispatch(setLoading());
    dispatch(getActiveDelegationSessionRequest());
  }
  dispatch(setErrorNoConnectionToBESide({ connectionUnavailable: false }));
  dispatch(setTokenExpired(false));
  stompClient.subscribe(`/user/${DESTINATION_QUEUE_MAIN}`, (message: Frame) => {
    onMessageReceived(message, dispatch, false, getState);
  });
  stompClient.subscribe(`/user/${DESTINATION_QUEUE_ERROR}`, (message: Frame) => {
    onMessageReceived(message, dispatch, true, getState);
  });
};

export const onDisconnect = (dispatch: Dispatch, frame: Frame): void => {
  console.warn('STOMP - disconnected', frame);
  dispatch(setConnected(false));
  dispatch(setWsUserSessionInitialized(false));
};

export const onStompError = (dispatch: Dispatch, frame: Frame): void => {
  console.error('STOMP - error', frame);
  if (frame.headers['message']?.includes('Incompatible version')) {
    window.location.reload();
  }
  dispatch(setConnected(false));
  dispatch(setWsConnectInitialized(false));
};

export const onWebSocketError = (dispatch: Dispatch, event: Event): void => {
  console.error('WebSocket - error', event);
  dispatch(setErrorNoConnectionToBESide({ connectionUnavailable: false }));
  dispatch(setWsConnectInitialized(false));
};

export const onWebSocketClose = (dispatch: Dispatch, event: Event): void => {
  console.warn('WebSocket - closed', event);
  dispatch(setConnected(false));
  dispatch(setWsUserSessionInitialized(false));
};
